import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { apiFetch, createWebSocket, getAccessToken } from '@/lib/api';
import { useAuth } from './AuthContext';

export interface NotificationItem {
  id: number;
  type: string;
  title: string;
  message?: string;
  read: number;
  created_at: string;
}

interface NotificationContextValue {
  notifications: NotificationItem[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markNotificationRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  const fetchNotifications = async () => {
    try {
      const res = await apiFetch('/api/notifications?limit=20');
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      // ignore
    }
  };

  const markNotificationRead = async (id: number) => {
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
      await fetchNotifications();
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await apiFetch('/api/notifications/read-all', { method: 'PUT' });
      await fetchNotifications();
    } catch (e) {}
  };

  const deleteNotification = async (id: number) => {
    try {
      await apiFetch(`/api/notifications/${id}`, { method: 'DELETE' });
      await fetchNotifications();
    } catch (e) {}
  };

  // WebSocket push listener
  useEffect(() => {
    if (!user) return;

    const connect = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      const token = getAccessToken();
      if (!token) return;

      const ws = createWebSocket();
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[Notifications] WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification') {
            setNotifications((prev) => [msg.data, ...prev].slice(0, 100));
            setUnreadCount((count) => count + 1);
          }
        } catch (e) {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Reconnect after 5 seconds
        setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user]);

  // Initial fetch and polling fallback
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        fetchNotifications,
        markNotificationRead,
        markAllRead,
        deleteNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
